import { createAsyncAction, createStandardAction } from 'typesafe-actions';

import generateLogger from '@travel/logger';

import { PLAN_LIST_SORT_VALUES } from 'constants/sort';
import { getIPAddress, isInSortCategory } from 'store/utils';
import { fetchPlanList as fetchPlanListAPI } from './apis';

import { getUserIdentifier } from 'store/common/selectors';
import { getSearchEngine } from 'store/searchEngine/selectors';

import type { AppThunk } from 'typesafe-actions';
import type { PlanList, PlanListErrors, RequestBody } from 'PlanList-Types';
const logger = generateLogger();

export const fetchPlanListAsync = createAsyncAction(
  'FETCH_PLAN_LIST_REQUEST',
  'FETCH_PLAN_LIST_SUCCESS',
  'FETCH_PLAN_LIST_FAILURE',
)<undefined, PlanList, PlanListErrors>();

export const fetchMorePlanListAsync = createAsyncAction(
  'FETCH_MORE_PLAN_LIST_REQUEST',
  'FETCH_MORE_PLAN_LIST_SUCCESS',
  'FETCH_MORE_PLAN_LIST_FAILURE',
)<undefined, PlanList, PlanListErrors>();

export const fetchPlanListFilterAsync = createAsyncAction(
  'FETCH_PLAN_LIST_FILTER_REQUEST',
  'FETCH_PLAN_LIST_FILTER_SUCCESS',
  'FETCH_PLAN_LIST_FILTER_FAILURE',
)<undefined, PlanList, undefined>();

export const updatePendingFilters = createStandardAction(
  'UPDATE_PLAN_LIST_PENDING_FILTERS',
)<PlanList>();

export const fetchPlanList =
  (query: RequestBody): AppThunk =>
  async (dispatch, getState, { apiClient, req }) => {
    dispatch(fetchPlanListAsync.request());
    const userIdentifier = getUserIdentifier(getState());
    const searchEngine = getSearchEngine(getState());

    try {
      const clonedQuery = { ...query };
      if (!isInSortCategory(PLAN_LIST_SORT_VALUES, clonedQuery.sort)) {
        clonedQuery.sort = PLAN_LIST_SORT_VALUES.Price_Low_To_High;
      }
      const response = await fetchPlanListAPI(
        apiClient,
        {
          ...clonedQuery,
          rzCookie: userIdentifier,
          searchEngine,
        },
        getIPAddress(req),
      );
      dispatch(fetchPlanListAsync.success(response));
    } catch (error) {
      logger.error(error);
      dispatch(fetchPlanListAsync.failure(error as PlanListErrors));
      throw error;
    }
  };

export const fetchMorePlans =
  (params: RequestBody): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    dispatch(fetchMorePlanListAsync.request());

    const state = getState();
    const { offset, limit } = state.planList.planList;
    const userIdentifier = getUserIdentifier(state);
    const searchEngine = getSearchEngine(getState());

    const clonedParams = { ...params };
    if (!isInSortCategory(PLAN_LIST_SORT_VALUES, clonedParams.sort)) {
      clonedParams.sort = PLAN_LIST_SORT_VALUES.Price_Low_To_High;
    }

    const requestBody = {
      ...clonedParams,
      offset: limit + offset,
      limit: limit,
      rzCookie: userIdentifier,
      searchEngine,
    };

    try {
      const response = await fetchPlanListAPI(apiClient, requestBody);
      dispatch(fetchMorePlanListAsync.success(response));
    } catch (error) {
      dispatch(fetchMorePlanListAsync.failure(error as PlanListErrors));
    }
  };

export const fetchPlanListFilter =
  (query: RequestBody): AppThunk =>
  async (dispatch, getState, { apiClient }) => {
    dispatch(fetchPlanListFilterAsync.request());
    const searchEngine = getSearchEngine(getState());
    try {
      const clonedQuery = { ...query };
      if (!isInSortCategory(PLAN_LIST_SORT_VALUES, clonedQuery.sort)) {
        clonedQuery.sort = PLAN_LIST_SORT_VALUES.Price_Low_To_High;
      }

      // limit could be 1 since we will not use providers data here
      const response = await fetchPlanListAPI(apiClient, {
        ...clonedQuery,
        offset: 0,
        limit: 1,
        searchEngine,
      });

      dispatch(fetchPlanListFilterAsync.success(response));
    } catch (error) {
      const prevState = getState().planList.planList;
      if (prevState) {
        dispatch(fetchPlanListFilterAsync.success(prevState));
      } else {
        dispatch(fetchPlanListFilterAsync.failure());
      }
    }
  };
